import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGallery } from "@/hooks/content";
import { useTranslatedList } from "@/hooks/useTranslatedList";

export default function GalleryPage() {
  const { t } = useTranslation();
  const { albums, images } = useGallery();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Gallery | L+TV TÉLÉVISION";
  }, []);

  const publishedAlbums = useTranslatedList(
    albums.items.filter((a) => a.status === "published"),
    ["name", "description", "category"],
  );
  const selectedAlbum = publishedAlbums.find((a) => a.id === selectedId) ?? null;
  const albumImages = useMemo(
    () => images.items.filter((img) => img.albumId === selectedId),
    [images.items, selectedId],
  );

  const totalPhotos = publishedAlbums.reduce(
    (sum, a) => sum + images.items.filter((img) => img.albumId === a.id).length,
    0,
  );

  return (
    <div className="pt-20">
      <div className="bg-primary-900 py-8">
        <div className="ltv-container">
          <h1 className="text-2xl md:text-3xl font-bold text-white">{t("gallery.title")}</h1>
          <p className="text-white/60 text-sm mt-1">
            {t("gallery.subtitle")}
          </p>
        </div>
      </div>

      <div className="ltv-container py-8 md:py-12">
        {albums.loading ? (
          <div className="text-center py-16 text-foreground-500">{t("gallery.loading")}</div>
        ) : publishedAlbums.length === 0 ? (
          <div className="text-center py-16">
            <i className="ri-image-line text-4xl text-foreground-300 mb-3"></i>
            <p className="text-foreground-500">{t("gallery.noAlbums")}</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-foreground-600 mb-6">
              {publishedAlbums.length} {t("gallery.albums")} · {totalPhotos} {t("gallery.photos")}
            </p>

            {/* Albums */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
              {publishedAlbums.map((a) => {
                const count = images.items.filter((img) => img.albumId === a.id).length;
                const active = a.id === selectedId;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setSelectedId(active ? null : a.id)}
                    className={`text-left bg-white rounded-lg border overflow-hidden transition-all ${
                      active ? "border-accent-400 ring-2 ring-accent-100" : "border-background-200 hover:border-primary-300"
                    }`}
                  >
                    <div className="relative h-44 bg-background-100 overflow-hidden">
                      {a.cover ? (
                        <img src={a.cover} alt={a.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-foreground-300">
                          <i className="ri-image-line text-2xl" />
                        </div>
                      )}
                      <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                        {count} {t("gallery.photos")}
                      </span>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-foreground-900">{a.name}</h3>
                      {a.category && (
                        <p className="text-xs text-foreground-500 mt-1">{a.category}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Selected album photos */}
            {selectedAlbum && (
              <div className="bg-white rounded-lg border border-background-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-bold text-foreground-900">{selectedAlbum.name}</h2>
                    {selectedAlbum.description && (
                      <p className="text-sm text-foreground-600 mt-1">{selectedAlbum.description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedId(null)}
                    className="text-sm font-medium text-foreground-600 hover:text-foreground-900"
                  >
                    {t("gallery.close")}
                  </button>
                </div>
                {albumImages.length === 0 ? (
                  <p className="text-sm text-foreground-500 py-8 text-center">
                    {t("gallery.noPhotos")}
                  </p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {albumImages.map((img) => (
                      <div key={img.id} className="relative rounded-md overflow-hidden bg-background-100">
                        <img src={img.url} alt={img.caption} className="w-full aspect-square object-cover" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}